import { formatDateDisplay } from '../utils/date';
import { LEGAL_ENTITY_NAME } from '../config/brandProfiles';

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="flex items-baseline gap-3 text-lg font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
        <span className="text-blue-600 font-bold tabular-nums">{number}.</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{title}</h3>
      <p>{children}</p>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-2 space-y-1.5 list-none">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="mt-0.5 text-blue-400 shrink-0">&#8227;</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-10 text-center">
          <p className="text-xl font-bold text-gray-900 mb-4">{LEGAL_ENTITY_NAME}</p>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Terms of Service</h1>
          <p className="text-sm text-gray-500">Last updated: {formatDateDisplay('2026-09-16')}</p>
        </div>

        {/* Intro card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-10 text-sm text-blue-900 leading-relaxed">
          By creating an account or using the parent portal for Bright Horizon Chinese School and
          Kaleido, you agree to be bound by these Terms of Service. If you do not agree, please do
          not use the portal. By creating an account you also consent to receive required notices,
          invoices, and disclosures from us electronically (see Section 7).
        </div>

        {/* Body */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-8 py-10">

          {/* ── 1. Description of Service ──────────────────────────── */}
          <Section number="1" title="Description of Service">
            <p>
              Bright Minds Collective LLC ("we," "us," "our") operates Bright Horizon Chinese
              School ("BHCS") and Kaleido, and provides an online portal (the "Portal") for parents
              and legal guardians to register students, enroll in after-school, enrichment, camp,
              and early-learning programs, and make payments for those programs. These Terms apply
              to your use of the Portal for either program. The Portal is an administrative tool. It does not
              guarantee the availability of any specific program, seat, or time slot, and it does
              not constitute a warranty of educational outcomes.
            </p>
          </Section>

          {/* ── 2. Eligibility and User Accounts ───────────────────── */}
          <Section number="2" title="Eligibility and User Accounts">
            <p>
              You must be at least 18 years old and the parent or legal guardian of any student
              you enroll through the Portal. By creating an account you represent that you meet
              these requirements.
            </p>
            <p>
              You agree to provide accurate, current, and complete information during registration
              and to keep that information up to date. You are responsible for all activity that
              occurs under your account. Keep your password confidential and do not share it with
              any third party. If you believe your account has been accessed without authorization,
              notify us promptly at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              .
            </p>
            <p>
              We may suspend your account — temporarily or permanently — for non-payment,
              violation of these Terms, or any other reason at our discretion.
            </p>
          </Section>

          {/* ── 3. Payments and Fees ────────────────────────────────── */}
          <Section number="3" title="Payments and Fees">
            <p>
              You agree to pay all fees for programs and services you enroll in through the
              Portal. Payments are processed via Stripe (credit/debit card and ACH bank transfer)
              and Zelle. By using our payment services, you also agree to the applicable terms of
              those third-party processors. We are not responsible for errors, outages, or
              delays caused by Stripe or any other payment processor.
            </p>

            <SubSection title="3.1 — Recurring Billing">
              Certain programs are billed on a monthly recurring basis. If you opt in to
              autopay for a monthly program, you authorize us to charge your saved payment
              method each billing cycle until you cancel your enrollment or revoke authorization
              in writing to{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              . Recurring charges will continue until your enrollment ends or authorization is
              revoked, whichever comes first.
            </SubSection>

            <SubSection title="3.2 — Failed Payments">
              If a payment is declined or an ACH transfer is returned, you remain responsible for
              the outstanding balance. We reserve the right to suspend enrollment access until
              the balance is resolved.
            </SubSection>

            <SubSection title="3.3 — Refund Policy">
              Refund eligibility varies by program and is communicated at the time of enrollment.
              Where a refund is approved, it will be returned to the original payment method or
              to your BHCS Cash balance, depending on how the original payment was made (see
              Section 4.3). We reserve the right to apply cancellation fees as disclosed at
              enrollment.
            </SubSection>

            <SubSection title="3.4 — Billing Disputes">
              If you believe a charge is incorrect, you must notify us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>{' '}
              within <strong>60 days</strong> of the charge date. We will investigate and respond
              within a reasonable time. Disputes raised after 60 days may not be honored.
            </SubSection>

            <SubSection title="3.5 — Fee Changes">
              We will provide at least 30 days' advance notice of changes to program fees
              before the next billing cycle is affected.
            </SubSection>
          </Section>

          {/* ── 4. BHCS Cash ────────────────────────────────────────── */}
          <Section number="4" title="BHCS Cash">
            <p>
              BHCS Cash is a prepaid balance feature that lets families deposit funds into their
              Portal account and apply those funds toward tuition, fees, and other invoices for
              either program. BHCS Cash is <strong>not</strong> a bank account, deposit account, or
              stored-value card. Balances are not FDIC-insured and do not earn interest.
            </p>
            <p>
              Deposits made by ACH bank transfer may take 3–5 business days to clear before the
              funds are available in your BHCS Cash balance.
            </p>

            <SubSection title="4.1 — Non-Withdrawable Credits">
              Credits we grant — including promotional bonuses, scholarships, and purchase
              bonuses — are non-withdrawable. They can only be applied toward invoices in the
              Portal and have no cash value outside the platform. They cannot be converted to
              cash, transferred, or refunded.
            </SubSection>

            <SubSection title="4.2 — Withdrawals">
              Only the withdrawable portion of your BHCS Cash balance (funds you deposited
              yourself) is eligible for withdrawal. Withdrawal requests must be submitted to{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>{' '}
              and are subject to administrative review, typically processed within 5–7 business
              days. We reserve the right to withhold a withdrawal if the account is under
              review or in dispute.
            </SubSection>

            <SubSection title="4.3 — Cancellation Refunds">
              If you cancel an enrollment that was paid using BHCS Cash, the refunded amount will
              be returned to your BHCS Cash balance in the same bucket from which it was spent
              (withdrawable or non-withdrawable), subject to the applicable program cancellation
              policy. Refunds for BHCS Cash payments are not sent to external payment methods.
            </SubSection>

            <SubSection title="4.4 — Account Termination">
              If your family account is closed, any remaining withdrawable balance will be
              refunded to you within a reasonable time using a payment method on file or by
              check. Any remaining non-withdrawable balance will be forfeited.
            </SubSection>

            <SubSection title="4.5 — Dormant Balances and Unclaimed Property">
              Under California's Unclaimed Property Law, prepaid balances that are inactive for
              three (3) years may be treated as unclaimed property and reported to the California
              State Controller's Office, after which the funds are remitted to the State. Before
              treating a balance as dormant, we will make reasonable efforts to notify you at
              your account email address. You may prevent dormancy by logging in and performing
              any account activity.
            </SubSection>

            <SubSection title="4.6 — Bonus and Promotional Credit">
              We may offer bonus credit on BHCS Cash purchases at our sole discretion. Bonus
              percentages and eligibility may change at any time without prior notice and are not
              guaranteed for future purchases.
            </SubSection>

            <SubSection title="4.7 — Liability">
              We are not liable for losses arising from unauthorized account access that results
              in balance misuse. You are responsible for the security of your login credentials.
              Report any suspected unauthorized use to{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>{' '}
              within 60 days of discovery.
            </SubSection>
          </Section>

          {/* ── 5. Children's Privacy and Student Data ──────────────── */}
          <Section number="5" title="Children's Privacy and Student Data">
            <p>
              Our programs serve children of all ages, including children under 13. Account
              holders must be the parent or legal guardian of any student whose information they
              submit through the Portal. By enrolling a student, you represent that you are that
              student's parent or legal guardian and that you consent to our collecting and
              using the student's information as described in these Terms.
            </p>
            <p>
              Student information (including name, date of birth, grade, school, and enrollment
              records, as applicable) is collected solely for the purpose of administering our
              programs. We do not
              sell student data, share it with third parties for commercial purposes, or use it
              for targeted advertising. Student data is shared only with service providers
              strictly necessary to operate the Portal (see Section 6).
            </p>
            <p>
              If you believe student information has been collected or used improperly, please
              contact us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              . We will investigate and respond promptly.
            </p>
          </Section>

          {/* ── 6. Privacy and Data Use ─────────────────────────────── */}
          <Section number="6" title="Privacy and Data Use">
            <p>
              When you use the Portal, we collect personal information including your name, email
              address, phone number, payment details, and information about the students you
              enroll.
            </p>

            <SubSection title="6.1 — How We Use Your Data">
              We use the information we collect to:
              <BulletList items={[
                'Create and manage your account',
                'Process enrollments and payments',
                'Send invoices, receipts, payment confirmations, and program communications',
                'Respond to your questions and support requests',
                'Comply with legal obligations',
              ]} />
            </SubSection>

            <SubSection title="6.2 — Third-Party Service Providers">
              We share your data only with the following third-party service providers, strictly
              for operating the Portal:
              <BulletList items={[
                'Stripe — payment processing (credit/debit card and ACH)',
                'Supabase — database hosting and authentication',
                'Google (Gmail API) — transactional email delivery',
              ]} />
              We do not sell your personal information or share it with any other third parties
              for marketing or commercial purposes.
            </SubSection>

            <SubSection title="6.3 — Data Retention">
              We retain account and transaction data for as long as your account is active
              and for a reasonable period thereafter to satisfy recordkeeping, legal, and
              accounting obligations. We do not routinely delete account records. If you have
              questions about your data, contact us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              .
            </SubSection>

            <SubSection title="6.4 — Security">
              We use industry-standard security practices to protect your personal information,
              including encrypted connections (HTTPS) and access controls. No system is perfectly
              secure; we cannot guarantee that unauthorized third parties will never be able to
              defeat our measures.
            </SubSection>
          </Section>

          {/* ── 7. Electronic Communications ────────────────────────── */}
          <Section number="7" title="Electronic Communications">
            <p>
              By creating an account, you consent to receive all required notices, invoices,
              payment confirmations, and other communications from us electronically, to the
              email address associated with your account. Electronic communications have the same
              legal effect as written communications. You are responsible for keeping your email
              address current. To withdraw this consent, contact us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              , though doing so may prevent us from delivering required notices and may affect
              your ability to use the Portal.
            </p>
          </Section>

          {/* ── 8. User Conduct ─────────────────────────────────────── */}
          <Section number="8" title="User Conduct">
            <p>You agree not to use the Portal:</p>
            <BulletList items={[
              'In any way that violates applicable federal, state, local, or international law or regulation',
              'To send advertising or promotional material without our prior written consent',
              'To impersonate or attempt to impersonate another person or entity',
              'To upload or transmit malicious code, viruses, or any software intended to damage or interfere with the Portal',
              'To use automated tools to scrape, crawl, or extract data from the Portal',
              'To interfere with or disrupt other users\' access to or enjoyment of the Portal',
            ]} />
          </Section>

          {/* ── 9. Intellectual Property ────────────────────────────── */}
          <Section number="9" title="Intellectual Property">
            <p>
              The Portal and all its contents — including software, text, images, and design —
              are owned by Bright Minds Collective LLC or its licensors and are protected by
              applicable intellectual property laws. Nothing in these Terms grants you any right
              to use the Bright Horizon Chinese School or Kaleido names, logos, or other
              proprietary materials.
            </p>
          </Section>

          {/* ── 10. Indemnification ─────────────────────────────────── */}
          <Section number="10" title="Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless Bright Minds Collective LLC and its
              officers, directors,
              employees, and volunteers from and against any claims, liabilities, damages, losses,
              and expenses (including reasonable legal fees) arising out of or related to: (a)
              your use of the Portal in violation of these Terms; (b) your violation of any
              applicable law or regulation; or (c) any claim by a third party arising from your
              actions or omissions in connection with the Portal.
            </p>
          </Section>

          {/* ── 11. Disclaimer of Warranties ────────────────────────── */}
          <Section number="11" title="Disclaimer of Warranties">
            <p>
              The Portal is provided <strong>"as is"</strong> and{' '}
              <strong>"as available"</strong> without warranties of any kind, express or implied,
              including warranties of merchantability, fitness for a particular purpose,
              uninterrupted access, or error-free operation. We do not warrant that the
              Portal will be available at any particular time, that defects will be corrected, or
              that the Portal or its servers are free of viruses or harmful components.
            </p>
            <p>
              We are not responsible for the failure of any third-party service, including
              Stripe, Supabase, or Gmail, to perform as expected.
            </p>
          </Section>

          {/* ── 12. Limitation of Liability ─────────────────────────── */}
          <Section number="12" title="Limitation of Liability">
            <p>
              To the fullest extent permitted by law, Bright Minds Collective LLC and its officers,
              directors, employees, and affiliates are not liable for any indirect, incidental,
              special, consequential, or punitive damages arising from your use of — or inability
              to use — the Portal, even if we have been advised of the possibility of such damages.
            </p>
            <p>
              In no event shall our total liability to you for any direct damages arising
              out of or related to these Terms or your use of the Portal exceed the greater of
              (a) the total fees you paid to us in the twelve (12) months preceding the
              claim, or (b) one hundred dollars ($100).
            </p>
          </Section>

          {/* ── 13. Termination ─────────────────────────────────────── */}
          <Section number="13" title="Termination">
            <p>
              We may suspend or terminate your account at any time, with or without notice, for
              any reason including a breach of these Terms. Upon termination, your right to use
              the Portal ceases immediately. Outstanding payment obligations survive termination
              — you remain responsible for any unpaid invoices at the time your account is closed.
            </p>
            <p>
              You may request closure of your account by contacting us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>
              . Upon voluntary closure, any remaining withdrawable BHCS Cash balance will be
              refunded as described in Section 4.4. We retain account and transaction records
              after closure as described in Section 6.3.
            </p>
          </Section>

          {/* ── 14. Dispute Resolution and Governing Law ────────────── */}
          <Section number="14" title="Dispute Resolution and Governing Law">
            <p>
              Before initiating any formal dispute, you agree to contact us at{' '}
              <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                billing@bhcskids.com
              </a>{' '}
              and give us 30 days to attempt to resolve the matter informally. Most concerns can
              be resolved this way.
            </p>
            <p>
              These Terms are governed by the laws of the State of California, without regard to
              its conflict-of-law provisions. Any dispute that cannot be resolved informally shall
              be brought exclusively in the state or federal courts located in San Mateo County,
              California, and you consent to the personal jurisdiction of those courts.
            </p>
          </Section>

          {/* ── 15. Changes to These Terms ──────────────────────────── */}
          <Section number="15" title="Changes to These Terms">
            <p>
              We may update these Terms at any time. For material changes, we will provide at
              least <strong>30 days' advance notice</strong> via email to your account address
              and/or a banner in the Portal before the new Terms take effect. Continued use of
              the Portal after an update constitutes your acceptance of the revised Terms.
              Non-material changes (such as corrections or clarifications) take effect
              immediately upon posting.
            </p>
          </Section>

          {/* ── 16. General Provisions ──────────────────────────────── */}
          <Section number="16" title="General Provisions">
            <p>
              <strong>Entire Agreement.</strong> These Terms constitute the entire agreement
              between you and Bright Minds Collective LLC regarding use of the Portal and supersede
              all prior
              understandings on that subject.
            </p>
            <p>
              <strong>Severability.</strong> If any provision of these Terms is found invalid or
              unenforceable, the remaining provisions continue in full force.
            </p>
            <p>
              <strong>No Waiver.</strong> Our failure to enforce any provision of these Terms is
              not a waiver of our right to enforce it in the future.
            </p>
            <p>
              <strong>Force Majeure.</strong> We are not liable for delays or failures caused
              by events beyond our reasonable control, including natural disasters, power
              outages, internet disruptions, or public health emergencies.
            </p>
          </Section>

          {/* Contact */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Contact Us</h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Bright Minds Collective LLC</p>
              <p>Operator of Bright Horizon Chinese School and Kaleido</p>
              <p>
                Email:{' '}
                <a href="mailto:billing@bhcskids.com" className="text-blue-700 hover:underline">
                  billing@bhcskids.com
                </a>
              </p>
              <p>Phone: 650-620-0299</p>
              <p>650 Shell Blvd, Foster City, CA 94404</p>
            </div>
          </div>

        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Bright Minds Collective LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default TermsOfService;
